import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Panel, Btn, Badge, Th, Td, inputCls, Field, Input, Textarea } from "@/admin/ui";

const EMPTY = { title: "", slug: "", excerpt: "", body: "", cover_image: "", author: "", tags: "", seo_title: "", seo_description: "" };

export default function InsightsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/api/admin/insights");
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({ id: null }); };
  const openEdit = (it) => {
    setForm({ title: it.title, slug: it.slug, excerpt: it.excerpt || "", body: it.body || "", cover_image: it.cover_image || "", author: it.author || "", tags: (it.tags || []).join(", "), seo_title: it.seo?.title || "", seo_description: it.seo?.description || "" });
    setEditing(it);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing.id) await api.put(`/api/admin/insights/${editing.id}`, payload);
      else await api.post("/api/admin/insights", payload);
      toast.success("Saved"); setEditing(null); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const togglePublish = async (it) => {
    try { await api.post(`/api/admin/insights/${it.id}/${it.status === "published" ? "unpublish" : "publish"}`); toast.success(it.status === "published" ? "Unpublished" : "Published"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (it) => { try { await api.delete(`/api/admin/insights/${it.id}`); toast.success("Deleted"); await load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="admin-insights">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink-heading">Insights</h1>
        <Btn onClick={openNew} data-testid="insight-new">New insight</Btn>
      </div>
      <Panel>
        <p className="mb-4 text-sm text-ink-muted">Public Insights (nav item + homepage section) appear automatically only when at least one article is published.</p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Title</Th><Th>Status</Th><Th>Updated</Th><Th></Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-slate-50" data-testid={`insight-row-${it.slug}`}>
                  <Td className="font-medium text-ink-heading">{it.title}</Td>
                  <Td><Badge status={it.status} /></Td>
                  <Td className="text-xs text-ink-muted">{(it.updated_at || "").slice(0, 10)}</Td>
                  <Td>
                    <div className="flex gap-2">
                      <Btn variant="ghost" onClick={() => openEdit(it)} data-testid={`insight-edit-${it.slug}`}>Edit</Btn>
                      <Btn variant={it.status === "published" ? "ghost" : "teal"} onClick={() => togglePublish(it)} data-testid={`insight-publish-${it.slug}`}>{it.status === "published" ? "Unpublish" : "Publish"}</Btn>
                      <Btn variant="danger" onClick={() => remove(it)} data-testid={`insight-delete-${it.slug}`}>Delete</Btn>
                    </div>
                  </Td>
                </tr>
              ))}
              {items.length === 0 && <tr><Td className="text-ink-muted">No insights yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="insight-editor">
            <h2 className="mb-4 text-lg font-semibold text-ink-heading">{editing.id ? "Edit insight" : "New insight"}</h2>
            <div className="space-y-4">
              <Field label="Title"><Input value={form.title} onChange={set("title")} data-testid="insight-title" /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Slug (optional)"><Input value={form.slug} onChange={set("slug")} placeholder="auto from title" /></Field>
                <Field label="Author"><Input value={form.author} onChange={set("author")} /></Field>
              </div>
              <Field label="Cover image URL"><Input value={form.cover_image} onChange={set("cover_image")} /></Field>
              <Field label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={set("excerpt")} /></Field>
              <Field label="Body"><Textarea rows={8} value={form.body} onChange={set("body")} data-testid="insight-body-input" /></Field>
              <Field label="Tags (comma separated)"><Input value={form.tags} onChange={set("tags")} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SEO title"><Input value={form.seo_title} onChange={set("seo_title")} /></Field>
                <Field label="SEO description"><Input value={form.seo_description} onChange={set("seo_description")} /></Field>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn onClick={save} disabled={busy} data-testid="insight-save">{busy ? "Saving…" : "Save"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
